import { z } from 'zod';
export const DbEngine = z.enum(['postgres', 'mysql', 'mongodb', 'redis']);
/**
 * The name of a secret file on the monitored server, never the secret itself.
 *
 * A bare name rather than a path, because the config document arrives from the API
 * and the agent opens this file with it. `../../var/lib/bartizo/agent.key` would
 * otherwise be a way for whoever can edit settings to have the agent read its own
 * private key and hand it to a database server of their choosing as a password. The
 * agent resolves the name inside its secrets directory and checks it again there;
 * this is the first of the two refusals, the one somebody sees in React.
 */
export const SecretRef = z
    .string()
    .regex(/^[a-z0-9][a-z0-9_-]{0,63}$/, 'must be a secret name like "postgres", not a path');
/**
 * One monitored database instance. A server can run several, so settings hold a list.
 *
 * `secret_ref` names a local secret file created during install; the secret itself
 * never appears here, because config documents travel over the network (rule 5).
 */
export const DbTarget = z.object({
    name: z.string().min(1).max(64),
    engine: DbEngine,
    host: z.string().max(255).optional(),
    port: z.number().int().positive().max(65535).optional(),
    socket: z.string().startsWith('/').max(1024).optional(),
    /**
     * The monitoring account. Not a secret, and each driver has a default — the
     * installer creates `bartizo_ro` — so it is only named when it differs.
     */
    user: z
        .string()
        .regex(/^[A-Za-z0-9_][A-Za-z0-9_.@-]{0,62}$/, 'must be a plain account name')
        .optional(),
    secret_ref: SecretRef.optional(),
    /** Limit which databases are read; default is every one the monitoring user can see. */
    databases: z.array(z.string().min(1).max(128)).max(100).optional(),
    /**
     * Section 8.4's "or a size limit": an event when the instance's total reaches
     * it — disk for the SQL engines and MongoDB, memory for Redis. Absent means no
     * limit; growth is still watched.
     */
    size_limit_bytes: z.number().int().min(1_000_000).max(Number.MAX_SAFE_INTEGER).optional(),
});
/**
 * What `install.sh` leaves at `/etc/bartizo/databases.json`: one target for each
 * monitoring account it created, naming its secret file and never the secret.
 *
 * A report, not settings. Section 11 has every setting come from the API, so the
 * agent monitors nothing because it is in this file; it reports the file in its
 * heartbeat, and the dashboard offers each entry as a target an administrator can
 * add with one click.
 */
export const DatabaseManifest = z.object({
    version: z.literal(1),
    targets: z.array(DbTarget).max(20),
});
//# sourceMappingURL=db-target.js.map